import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Spinner } from 'react-bootstrap';
import axios from 'axios';
import Select from 'react-select';

const DnsForm = ({
    showModal,
    handleClose,
    domainId,
    domainName,
    zoneId,
    instanceIp,
    dnsRecordId,
    dnsRecordData,
    addDnsRecord,
    editDnsRecord,
}) => {
    const [loading, setLoading] = useState(false);
    const [loadingSetups, setLoadingSetups] = useState(false);
    const [setups, setSetups] = useState([]);
    const [isCustomContent, setIsCustomContent] = useState(false);
    const [formData, setFormData] = useState({
        domain_id: domainId,
        setup_id: '',
        type: 'A',
        name: '',
        content: '',
        ttl: 3600,
        proxied: false,
    });
    const [errors, setErrors] = useState({});

    // Fetch setups when modal opens
    useEffect(() => {
        const fetchSetups = async () => {
            setLoadingSetups(true);
            try {
                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                const userInfo = JSON.parse(localStorage.getItem('user-info') || '{}');
                const origin = localStorage.getItem('user-origin');
                const token = userInfo?.token;

                const res = await axios.get(`${backendUrl}/api/v360-setup-info`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                    skipLoader: true,
                });
                const list = res.data?.data || (Array.isArray(res.data) ? res.data : []);
                setSetups(list);
            } catch (err) {
                console.error('Error fetching setups:', err);
            } finally {
                setLoadingSetups(false);
            }
        };

        if (showModal) {
            fetchSetups();
        }
    }, [showModal]);

    useEffect(() => {
        if (showModal) {
            if (dnsRecordData) {
                const recSetupId = dnsRecordData.setup_id || dnsRecordData.setup?.id || '';
                setFormData({
                    domain_id: dnsRecordData.domain_id || domainId,
                    setup_id: recSetupId ? String(recSetupId) : '',
                    type: dnsRecordData.type || 'A',
                    name: dnsRecordData.name || '',
                    content: dnsRecordData.content || '',
                    ttl: dnsRecordData.ttl || 3600,
                    proxied: dnsRecordData.proxied === 1 || dnsRecordData.proxied === true,
                });
            } else {
                setFormData({
                    domain_id: domainId,
                    setup_id: '',
                    type: 'A',
                    name: '',
                    content: '',
                    ttl: 3600,
                    proxied: false,
                });
                setIsCustomContent(false);
            }
            setErrors({});
        }
    }, [showModal, dnsRecordData, domainId]);

    // Sort setups alphabetically by client_name
    const sortedSetups = [...setups].sort((a, b) => {
        const nameA = (a.client_name || '').toLowerCase();
        const nameB = (b.client_name || '').toLowerCase();
        return nameA.localeCompare(nameB);
    });

    const setupOptions = sortedSetups.map(s => ({
        value: s.id,
        label: `${s.client_name}`,
        setup: s,
    }));

    const selectedSetupOption = setupOptions.find(opt => String(opt.value) === String(formData.setup_id)) || null;

    const activeSetup = setups.find(s => String(s.id) === String(formData.setup_id));
    const availableIps = Array.isArray(activeSetup?.setup_ips) ? activeSetup.setup_ips : [];

    // When editing an existing record, check if its content matches any setup IP
    useEffect(() => {
        if (showModal && dnsRecordData?.content && availableIps.length > 1) {
            const matchesSetupIp = availableIps.some(ip => ip.ip_address === dnsRecordData.content);
            if (!matchesSetupIp) {
                setIsCustomContent(true);
            }
        }
    }, [showModal, dnsRecordData, availableIps]);

    const handleSetupSelectChange = (selectedOption) => {
        const newSetupId = selectedOption ? String(selectedOption.value) : '';
        const matched = selectedOption?.setup;
        const ips = Array.isArray(matched?.setup_ips) ? matched.setup_ips : [];

        setIsCustomContent(false);
        setFormData((prev) => {
            let updatedContent = prev.content;
            if (newSetupId && ips.length > 0) {
                updatedContent = ips[0].ip_address;
            }
            return {
                ...prev,
                setup_id: newSetupId,
                content: updatedContent,
            };
        });

        if (errors.setup_id) {
            setErrors(prev => ({ ...prev, setup_id: undefined }));
        }
        if (errors.content) {
            setErrors(prev => ({ ...prev, content: undefined }));
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => {
            let updatedValue = type === 'checkbox' ? checked : value;
            let updatedContent = prev.content;
            
            if (name === 'type' && updatedValue === 'CNAME') {
                updatedContent = '';
            }

            return {
                ...prev,
                [name]: updatedValue,
                ...(name === 'type' ? { content: updatedContent } : {})
            };
        });
        
        if (errors[name]) {
            setErrors((prevErrors) => ({ ...prevErrors, [name]: undefined }));
        }
    };

    const getFinalDomainDisplay = () => {
        const cleanDomain = domainName && domainName !== 'undefined' && domainName !== 'null' ? String(domainName).trim() : '';
        const cleanName = (formData.name || '').trim();

        if (!cleanDomain) {
            return cleanName || '[subdomain]';
        }
        if (!cleanName || cleanName === '@') {
            return cleanDomain;
        }
        if (cleanName === cleanDomain || cleanName.endsWith(`.${cleanDomain}`)) {
            return cleanName;
        }
        return `${cleanName}.${cleanDomain}`;
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.type) newErrors.type = 'Type is required';
        if (!formData.name) {
            newErrors.name = 'Name is required';
        } else if (!/^[a-zA-Z0-9.-]+$/.test(formData.name)) {
            newErrors.name = 'Invalid format. Use alphanumeric, dots, and hyphens only.';
        }
        if (!formData.content) newErrors.content = 'Content (IP/Target) is required';
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setLoading(false);
            return;
        }

        const payload = {
            ...formData,
            setup_id: formData.setup_id ? Number(formData.setup_id) : null,
        };

        try {
            let apiErrors;
            if (dnsRecordId) {
                apiErrors = await editDnsRecord(dnsRecordId, payload);
            } else {
                apiErrors = await addDnsRecord(payload);
            }

            if (apiErrors && Object.keys(apiErrors).length > 0) {
                setErrors(apiErrors);
            } else {
                setErrors({});
                handleClose();
            }
        } catch (error) {
            console.error("An error occurred:", error);
        } finally {
            setLoading(false);
        }
    };

    const controlStyle = {
        fontSize: '14px',
        borderRadius: '14px',
        height: '39px',
        border: '0.5px solid rgb(232, 233, 237)'
    };

    const selectCustomStyles = {
        control: (base, state) => ({
            ...base,
            fontSize: '14px',
            borderRadius: '14px',
            minHeight: '39px',
            height: '39px',
            border: state.isFocused ? '1px solid #86b7fe' : '0.5px solid rgb(232, 233, 237)',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.15)' : 'none',
            '&:hover': {
                border: '0.5px solid #b0b3b8'
            }
        }),
        valueContainer: (base) => ({
            ...base,
            height: '39px',
            padding: '0 12px'
        }),
        input: (base) => ({
            ...base,
            margin: '0px'
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '39px'
        }),
        menu: (base) => ({
            ...base,
            fontSize: '14px',
            zIndex: 9999
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        })
    };

    return (
        <Offcanvas 
            show={showModal} 
            onHide={handleClose} 
            placement="end" 
            style={{ width: '490px' }} 
            backdrop="static"
            className="dns-record-offcanvas"
        >
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>
                    {dnsRecordId ? 'Edit DNS Record' : 'Add DNS Record'}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <hr style={{ margin: 0 }} />

            <Offcanvas.Body>
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Type:</Form.Label>
                        <Form.Select
                            name="type"
                            value={formData.type}
                            onChange={handleChange}
                            className={`form-control ${errors?.type ? 'is-invalid' : ''}`}
                            style={controlStyle}
                            required
                        >
                            <option value="A">A</option>
                            <option value="CNAME">CNAME</option>
                        </Form.Select>
                        {errors.type && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.type}</div>}
                    </Form.Group>

                    {/* Setup Name Searchable Dropdown */}
                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Setup Name:</Form.Label>
                        <Select
                            options={setupOptions}
                            value={selectedSetupOption}
                            onChange={handleSetupSelectChange}
                            isSearchable={true}
                            isClearable={true}
                            isLoading={loadingSetups}
                            placeholder="Search or select setup..."
                            styles={selectCustomStyles}
                            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        />
                        {formData.setup_id && availableIps.length === 0 && !loadingSetups && (
                            <small className="text-muted d-block mt-1" style={{ fontSize: '11px' }}>
                                No setup IP addresses registered for this setup.
                            </small>
                        )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>Name (Subdomain):</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder="e.g. api or @"
                            value={formData.name}
                            onChange={handleChange}
                            className={`form-control ${errors?.name ? 'is-invalid' : ''}`}
                            style={controlStyle}
                            required
                        />
                        <div className="mt-1" style={{ fontSize: '12px', color: '#6c757d' }}>
                            <i className="bx bx-link-alt me-1"></i>
                            Final Domain: <strong className="text-primary">
                                {getFinalDomainDisplay()}
                            </strong>
                        </div>
                        {errors.name && <div className="text-danger mt-1" style={{ fontSize: '12px' }}>*{errors.name}</div>}
                    </Form.Group>

                    {/* Content field - directly renders as a dropdown if the setup has multiple IPs */}
                    <Form.Group className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-1">
                            <Form.Label style={{ fontSize: '13px' }} className="mb-0">Content:</Form.Label>
                            {availableIps.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-decoration-none"
                                    style={{ fontSize: '11px' }}
                                    onClick={() => {
                                        setIsCustomContent(prev => !prev);
                                        if (!isCustomContent) {
                                            setFormData(p => ({ ...p, content: '' }));
                                        } else if (availableIps.length > 0) {
                                            setFormData(p => ({ ...p, content: availableIps[0].ip_address }));
                                        }
                                    }}
                                >
                                    {isCustomContent ? 'Choose from Setup IPs' : '+ Custom IP'}
                                </button>
                            )}
                            {availableIps.length === 1 && (
                                <small className="text-success" style={{ fontSize: '11px' }}>
                                    <i className="mdi mdi-check-circle me-1"></i>
                                    Setup IP: {availableIps[0].ip_address} {availableIps[0].remark ? `(${availableIps[0].remark})` : ''}
                                </small>
                            )}
                        </div>

                        {availableIps.length > 1 && !isCustomContent ? (
                            <Form.Select
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                className={`form-control ${errors?.content ? 'is-invalid' : ''}`}
                                style={controlStyle}
                                required
                            >
                                <option value="">-- Select Content IP --</option>
                                {availableIps.map((ip, idx) => (
                                    <option key={idx} value={ip.ip_address}>
                                        {ip.ip_address} {ip.remark ? `— ${ip.remark}` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : (
                            <Form.Control
                                type="text"
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                className={`form-control ${errors?.content ? 'is-invalid' : ''}`}
                                style={controlStyle}
                                required
                            />
                        )}
                        {errors.content && <div className="invalid-feedback d-block" style={{ fontSize: '12px' }}>*{errors.content}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label style={{ fontSize: '13px' }}>TTL:</Form.Label>
                        <Form.Control
                            type="number"
                            name="ttl"
                            value={formData.ttl}
                            onChange={handleChange}
                            style={controlStyle}
                        />
                    </Form.Group>
             
                    <Form.Group className="mb-3 mt-4">
                        <Form.Check 
                            type="switch"
                            id="proxied-switch"
                            label="Proxied"
                            name="proxied"
                            checked={formData.proxied}
                            onChange={handleChange}
                        />
                    </Form.Group><br/><br/>

                    <div className="d-flex justify-content-end mt-4">
                        <Button variant="secondary" onClick={handleClose} className="me-2">
                            Close
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Save'}
                        </Button>
                    </div>
                </Form>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default DnsForm;
